import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { X, Plus, Trash2, Edit2, Save, Loader2, Users, Building2, User } from 'lucide-react';

const emptyCustomer = () => ({
  customer_type: 'company',
  company_name: '',
  org_number: '',
  vat_number: '',
  contact_person: '',
  email: '',
  phone: '',
  billing_address: '',
  postal_code: '',
  city: '',
  country: 'Sverige',
  notes: '',
});

const CustomerManagerModal = ({ onClose }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | customer.id
  const [form, setForm] = useState(emptyCustomer());
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.listInvoiceCustomers();
      setCustomers(res.data);
    } catch {
      toast.error('Kunde inte hämta kunder');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const startNew = () => { setForm(emptyCustomer()); setEditing('new'); };
  const startEdit = (c) => { setForm({ ...c }); setEditing(c.id); };

  const handleSave = async () => {
    if (form.customer_type === 'company' && !form.company_name) { toast.error('Företagsnamn krävs'); return; }
    if (form.customer_type === 'person' && !form.contact_person) { toast.error('Namn krävs'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.createInvoiceCustomer(form);
        toast.success('Kund tillagd');
      } else {
        await api.updateInvoiceCustomer(editing, form);
        toast.success('Sparat');
      }
      setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Misslyckades');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ta bort kund?')) return;
    try {
      await api.deleteInvoiceCustomer(id);
      toast.success('Borttagen');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Misslyckades');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-3 py-6" onClick={onClose} data-testid="customer-manager">
      <div className="bg-card rounded-2xl shadow-2xl border border-border max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#1A1A1A] text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-base font-bold flex items-center gap-2"><Users size={18} className="text-[#E8613A]" /> Kundregister</h2>
          <button onClick={onClose} className="hover:bg-white/10 rounded p-1"><X size={16} /></button>
        </div>

        {editing ? (
          <div className="overflow-y-auto p-6 space-y-3" data-testid="customer-form">
            <div className="flex gap-2 mb-2">
              <button onClick={() => setForm(f => ({ ...f, customer_type: 'company' }))}
                className={`flex-1 py-2 rounded-lg text-xs font-bold border ${form.customer_type === 'company' ? 'bg-[#E8613A] text-white border-[#E8613A]' : 'border-border'}`}
                data-testid="type-company">
                <Building2 size={12} className="inline mr-1" /> Företagskund
              </button>
              <button onClick={() => setForm(f => ({ ...f, customer_type: 'person' }))}
                className={`flex-1 py-2 rounded-lg text-xs font-bold border ${form.customer_type === 'person' ? 'bg-[#E8613A] text-white border-[#E8613A]' : 'border-border'}`}
                data-testid="type-person">
                <User size={12} className="inline mr-1" /> Privatperson
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {form.customer_type === 'company' && (
                <>
                  <Field label="Företagsnamn *" v={form.company_name} on={(v) => setForm({ ...form, company_name: v })} testid="c-name" />
                  <Field label="Org.nr" v={form.org_number} on={(v) => setForm({ ...form, org_number: v })} testid="c-org" />
                </>
              )}
              <Field label={form.customer_type === 'company' ? 'Kontaktperson' : 'Namn *'} v={form.contact_person} on={(v) => setForm({ ...form, contact_person: v })} testid="c-contact" />
              <Field label="E-post" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="c-email" />
              <Field label="Telefon" v={form.phone} on={(v) => setForm({ ...form, phone: v })} testid="c-phone" />
              <Field label="Adress" v={form.billing_address} on={(v) => setForm({ ...form, billing_address: v })} testid="c-addr" />
              <Field label="Postnr" v={form.postal_code} on={(v) => setForm({ ...form, postal_code: v })} testid="c-postal" />
              <Field label="Ort" v={form.city} on={(v) => setForm({ ...form, city: v })} testid="c-city" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">Avbryt</Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="c-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1.5" /> Spara</>}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <p className="text-sm text-muted-foreground p-6 text-center">Laddar...</p>
              ) : customers.length === 0 ? (
                <div className="p-10 text-center">
                  <Users size={32} className="mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">Inga kunder än. Lägg till din första kund för att kunna skapa fakturor.</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {customers.map(c => (
                    <div key={c.id} className="px-4 py-3 flex items-center justify-between hover:bg-muted/30" data-testid={`cust-${c.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {c.customer_type === 'company' ? <Building2 size={16} className="text-muted-foreground flex-shrink-0" /> : <User size={16} className="text-muted-foreground flex-shrink-0" />}
                        <div className="min-w-0">
                          <p className="font-medium truncate">{c.company_name || c.contact_person || '—'}</p>
                          <p className="text-xs text-muted-foreground truncate">{c.email || c.phone || c.org_number || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => startEdit(c)} className="p-2 hover:text-[#E8613A]" data-testid={`edit-${c.id}`}><Edit2 size={13} /></button>
                        <button onClick={() => handleDelete(c.id)} className="p-2 hover:text-red-600" data-testid={`del-${c.id}`}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-border bg-muted/30">
              <Button onClick={startNew} className="w-full bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full" data-testid="add-customer-btn">
                <Plus size={14} className="mr-1.5" /> Lägg till kund
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, v, on, testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input value={v || ''} onChange={(e) => on(e.target.value)} className="mt-1" data-testid={testid} />
  </div>
);

export default CustomerManagerModal;
